import { createRef } from 'react';

import { Logger } from '../../models/logger';
import { FileUpload } from '../../models/file-upload';
import { UploadInProgressFile } from '../../models/upload-in-progress-file';
import { FileSelectEvent } from '../../types/file-upload.types';
import { FromTypes, ToTypes } from '../../types/types';
import { DateTimeUtils } from '../../ui-utils/date-time.utils';
import { getAudioCodecForExtension, getVideoCodecForExtension } from '../../utils/codec-registry';
import { getExtension } from '../../utils/file';
import { FileUploadUtils } from '../../utils/file-upoad.utils';
import { includes } from '../../utils/lodash-min';
import { MotiveLocationTypes } from '../../utils/request.utils';
import { SupportedFileTypes } from '../converter/file-upload-manager';
import FileUploadComponent from '../converter/file-upload/file-upload.component';
import Dropdown from '../ui/dropdown/dropdown';
import { FeatureDetailsComponent } from '../ui/feature-details/feature-details.component';
import FeaturesTilesComponent from '../ui/features-tiles/features-tiles.component';
import { FileListItemComponent } from '../ui/file-list/file-list-item.component';
import { MotiveComponent } from '../ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import RatingDisplayComponent from '../ui/rating-display/rating-display.component';
import { RelatedLinksComponent } from '../ui/related-links/related-links.component';
import { AVBaseComponent, AVBaseState, PLAYABLE_FILE_MIMI_TYPES } from '../shared/av-base.component';
import styles from './av-cutter.component.module.scss';

export interface CutSegment {
    id: string;
    start: number;
    end: number;
    action: 'keep' | 'remove';
}

interface AVCutterState extends AVBaseState {
    segments: CutSegment[];
    isPlayingPreview: boolean;
    showOutputPreview: boolean;
    isDragging: boolean;
}

export default class AVCutterComponent extends AVBaseComponent<AVCutterState> {
    protected readonly logger = Logger.getLogger('AVCutterComponent');

    cutFileUploadArray: FileUpload[] = [];
    previewCanvasRef = createRef<HTMLCanvasElement>();
    previewAnimationFrameId: number | null = null;

    readonly state: Readonly<AVCutterState> = {
        fileSelected: false,
        fakeUploadInProgressFileForSelectedFiles: [],
        fileWithMetadataList: [],
        isPlaying: false,
        isMuted: false,
        isLoading: false,
        segments: [],
        isPlayingPreview: false,
        showOutputPreview: false,
        isDragging: false,
    };

    get isFileCutInProgress(): boolean {
        return this.cutFileUploadArray && this.cutFileUploadArray.length > 0 && this.cutFileUploadArray.some(f => !f.isCompleted() && !f.isFailed());
    }

    get hasKeepSegments(): boolean {
        return this.state.segments.some(seg => seg.action === 'keep');
    }

    componentWillUnmount() {
        if (this.previewAnimationFrameId) {
            cancelAnimationFrame(this.previewAnimationFrameId);
        }
    }

    playOrPauseOutputVideo() {
        if (this.outputVideoElement.paused) {
            this.outputVideoElement.play();
            this.setState({ isPlaying: true });
        } else {
            this.outputVideoElement.pause();
            this.setState({ isPlaying: false });
        }
    }

    toggleMute() {
        this.outputVideoElement.muted = !this.outputVideoElement.muted;
        this.setState({ isMuted: this.outputVideoElement.muted });
    }

    handleScrub(event: any) {
        const time = parseFloat(event.target.value);
        this.outputVideoElement.currentTime = time;
        this.forceUpdate();
    }

    seekOutputVideo(seconds: number) {
        const newTime = this.outputVideoElement.currentTime + seconds;
        if (newTime < 0) {
            this.outputVideoElement.currentTime = 0;
        } else if (newTime > this.videoDuration) {
            this.outputVideoElement.currentTime = this.videoDuration;
        } else {
            this.outputVideoElement.currentTime = newTime;
        }
        this.forceUpdate();
    }

    handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.state.isDragging) {
            this.setState({ isDragging: true });
        }
    };

    handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: false });
    };

    handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: false });

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            this.fileSelected({ files: [files[0]] });
        }
    };

    videoLoaded() {
        this.videoDuration = this.outputVideoElement.duration;
        // Initialize with one segment covering the entire video (set to remove by default)
        const initialSegment: CutSegment = {
            id: this.generateSegmentId(),
            start: 0,
            end: this.videoDuration,
            action: 'keep'
        };
        this.setState({ segments: [initialSegment], showOutputPreview: true });
    }

    generateSegmentId(): string {
        return `segment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    seekToTimelinePosition(event: React.MouseEvent<HTMLDivElement>) {
        if (!this.videoDuration) return;

        const timeline = event.currentTarget;
        const rect = timeline.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const percentage = clickX / rect.width;
        const newTime = percentage * this.videoDuration;

        this.outputVideoElement.currentTime = Math.max(0, Math.min(this.videoDuration, newTime));
        this.forceUpdate();
    }

    private isDragging = false;
    private dragStartX = 0;
    private resizingSegment: {
        id: string;
        edge: 'start' | 'end';
        initialTime: number;
        segmentIndex: number;
        currentSegments: CutSegment[];
    } | null = null;

    handlePlayheadDragStart = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        this.isDragging = false;
        const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
        this.dragStartX = clientX;

        let rafId: number | null = null;
        let needsUpdate = false;

        const handleDrag = (e: MouseEvent | TouchEvent) => {
            if (!this.videoDuration) return;

            const currentClientX = 'touches' in e ? e.touches[0].clientX : e.clientX;

            // Consider it a drag if moved more than 5 pixels
            if (Math.abs(currentClientX - this.dragStartX) > 5) {
                this.isDragging = true;
            }

            const timeline = document.querySelector(`.${styles['timeline']}`) as HTMLElement;
            if (!timeline) return;

            const rect = timeline.getBoundingClientRect();
            const dragX = currentClientX - rect.left;
            const percentage = Math.max(0, Math.min(1, dragX / rect.width));
            const newTime = percentage * this.videoDuration;

            // Update video currentTime immediately
            this.outputVideoElement.currentTime = newTime;

            // Throttle React re-renders to 60fps
            needsUpdate = true;
            if (rafId === null) {
                rafId = requestAnimationFrame(() => {
                    if (needsUpdate) {
                        this.forceUpdate();
                        needsUpdate = false;
                    }
                    rafId = null;
                });
            }
        };

        const handleDragEnd = () => {
            // Cancel any pending animation frame
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }

            // Final update if needed
            if (needsUpdate) {
                this.forceUpdate();
            }

            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('mouseup', handleDragEnd);
            document.removeEventListener('touchmove', handleDrag);
            document.removeEventListener('touchend', handleDragEnd);
            document.removeEventListener('touchcancel', handleDragEnd);

            // Reset drag state after a brief delay to prevent accidental clicks
            setTimeout(() => {
                this.isDragging = false;
            }, 100);
        };

        document.addEventListener('mousemove', handleDrag);
        document.addEventListener('mouseup', handleDragEnd);
        document.addEventListener('touchmove', handleDrag);
        document.addEventListener('touchend', handleDragEnd);
        document.addEventListener('touchcancel', handleDragEnd);
    };

    handleCutClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        // Don't cut if we just finished dragging
        if (this.isDragging) {
            return;
        }

        this.addSegmentAtCurrentTime();
    };

    addSegmentAtCurrentTime() {
        const currentTime = this.outputVideoCurrentTime;
        const segments = [...this.state.segments];
        const MIN_SEGMENT_DURATION = 0.5;

        // Find which segment contains the current time
        const segmentIndex = segments.findIndex(seg => currentTime >= seg.start && currentTime <= seg.end);

        if (segmentIndex === -1) {
            // Current time is in a gap - find the gap boundaries and create a new segment
            const sortedSegments = [...segments].sort((a, b) => a.start - b.start);

            let gapStart = 0;
            let gapEnd = this.videoDuration;

            // Find the gap that contains currentTime
            for (let i = 0; i < sortedSegments.length; i++) {
                const seg = sortedSegments[i];
                if (seg.end <= currentTime) {
                    // This segment ends before currentTime, gap starts after it
                    gapStart = seg.end;
                } else if (seg.start > currentTime) {
                    // This segment starts after currentTime, gap ends before it
                    gapEnd = seg.start;
                    break;
                }
            }

            // Check if we can create valid segments (each with min duration)
            const firstSegmentDuration = currentTime - gapStart;
            const secondSegmentDuration = gapEnd - currentTime;

            // If either segment would be too small, just create one segment covering the gap
            if (firstSegmentDuration < MIN_SEGMENT_DURATION || secondSegmentDuration < MIN_SEGMENT_DURATION) {
                const newSegment: CutSegment = { id: this.generateSegmentId(), start: gapStart, end: gapEnd, action: 'keep' };
                this.setState({ segments: [...segments, newSegment].sort((a, b) => a.start - b.start) });
                return;
            }

            // Create a new segment in the gap, split at currentTime
            const newSegment1: CutSegment = {
                id: this.generateSegmentId(),
                start: gapStart,
                end: currentTime,
                action: 'remove'
            };
            const newSegment2: CutSegment = {
                id: this.generateSegmentId(),
                start: currentTime,
                end: gapEnd,
                action: 'keep'
            };

            this.setState({ segments: [...segments, newSegment1, newSegment2].sort((a, b) => a.start - b.start) });
            return;
        }

        const targetSegment = segments[segmentIndex];

        // Check if split would create segments that are too small
        const firstPartDuration = currentTime - targetSegment.start;
        const secondPartDuration = targetSegment.end - currentTime;

        if (firstPartDuration < MIN_SEGMENT_DURATION || secondPartDuration < MIN_SEGMENT_DURATION) {
            return; // Would create segment(s) that are too small
        }

        // Split the segment at current time
        const newSegment1: CutSegment = {
            ...targetSegment,
            end: currentTime
        };

        const newSegment2: CutSegment = {
            id: this.generateSegmentId(),
            start: currentTime,
            end: targetSegment.end,
            action: targetSegment.action === 'keep' ? 'remove' : 'keep' // Toggle action for split
        };

        segments.splice(segmentIndex, 1, newSegment1, newSegment2);
        this.setState({ segments });
    }

    removeSegment(segmentId: string) {
        const segments = [...this.state.segments];
        const segmentIndex = segments.findIndex(seg => seg.id === segmentId);

        if (segmentIndex === -1) return;

        if (segments.length === 1) {
            // Don't allow removing all segments, reset to full video
            this.videoLoaded();
            return;
        }

        const removedSegment = segments[segmentIndex];

        // Remove the segment
        segments.splice(segmentIndex, 1);

        // Adjust adjacent segments to fill the gap
        if (segmentIndex === 0) {
            // Removed first segment - expand the next segment to start at 0
            segments[0].start = 0;
        } else if (segmentIndex === segments.length) {
            // Removed last segment - expand the previous segment to end at video duration
            segments[segments.length - 1].end = this.videoDuration;
        } else {
            // Removed middle segment - expand the previous segment to cover the gap
            segments[segmentIndex - 1].end = removedSegment.end;
        }

        this.setState({ segments });
    }

    toggleSegmentAction(segmentId: string) {
        const segments = this.state.segments.map(seg => {
            if (seg.id === segmentId) {
                return { ...seg, action: seg.action === 'keep' ? 'remove' : 'keep' } as CutSegment;
            }
            return seg;
        });
        this.setState({ segments });
    }

    handleSegmentResizeStart = (segmentId: string, edge: 'start' | 'end', event: React.MouseEvent | React.TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const segmentIndex = this.state.segments.findIndex(s => s.id === segmentId);
        if (segmentIndex === -1) return;

        const segment = this.state.segments[segmentIndex];

        // Store a working copy of segments for smooth DOM updates
        this.resizingSegment = {
            id: segmentId,
            edge,
            initialTime: edge === 'start' ? segment.start : segment.end,
            segmentIndex,
            currentSegments: JSON.parse(JSON.stringify(this.state.segments))
        };

        // Disable text selection and pointer events on segments during drag
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'ew-resize';

        // Disable pointer events on all segments so they don't interfere with resize
        const allSegmentElements = document.querySelectorAll(`.${styles['segment']}`);
        allSegmentElements.forEach(seg => {
            (seg as HTMLElement).style.pointerEvents = 'none';
        });

        let rafId: number | null = null;
        let needsStateUpdate = false;

        const handleResize = (e: MouseEvent | TouchEvent) => {
            if (!this.resizingSegment || !this.videoDuration) return;

            const timeline = document.querySelector(`.${styles['timeline']}`) as HTMLElement;
            if (!timeline) return;

            const rect = timeline.getBoundingClientRect();
            const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
            const mouseX = clientX - rect.left;
            const percentage = Math.max(0, Math.min(1, mouseX / rect.width));
            let newTime = percentage * this.videoDuration;

            const MIN_SEGMENT_DURATION = 0.5;
            const segments = this.resizingSegment.currentSegments;
            const segIdx = this.resizingSegment.segmentIndex;
            const currentSegment = segments[segIdx];

            // Apply constraints
            newTime = Math.max(0, Math.min(this.videoDuration, newTime));

            if (this.resizingSegment.edge === 'start') {
                const prevSegment = segIdx > 0 ? segments[segIdx - 1] : null;
                const maxStart = currentSegment.end - MIN_SEGMENT_DURATION;
                let minStart = 0;
                if (prevSegment) {
                    minStart = prevSegment.start + MIN_SEGMENT_DURATION;
                }
                newTime = Math.max(minStart, Math.min(maxStart, newTime));

                currentSegment.start = newTime;
                if (prevSegment) {
                    prevSegment.end = newTime;
                }
            } else {
                const nextSegment = segIdx < segments.length - 1 ? segments[segIdx + 1] : null;
                const minEnd = currentSegment.start + MIN_SEGMENT_DURATION;
                let maxEnd = this.videoDuration;
                if (nextSegment) {
                    maxEnd = nextSegment.end - MIN_SEGMENT_DURATION;
                }
                newTime = Math.max(minEnd, Math.min(maxEnd, newTime));

                currentSegment.end = newTime;
                if (nextSegment) {
                    nextSegment.start = newTime;
                }
            }

            // Update DOM directly for instant smooth visual feedback on timeline
            this.updateSegmentDOMPositions(segments);

            // Schedule React state update for segment cards (throttled to 60fps)
            needsStateUpdate = true;
            if (rafId === null) {
                rafId = requestAnimationFrame(() => {
                    if (needsStateUpdate && this.resizingSegment) {
                        this.setState({ segments: [...this.resizingSegment.currentSegments] });
                        needsStateUpdate = false;
                    }
                    rafId = null;
                });
            }
        };

        const handleResizeEnd = (e: MouseEvent | TouchEvent) => {
            // Cancel any pending animation frame
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }

            // Remove event listeners
            document.removeEventListener('mousemove', handleResize);
            document.removeEventListener('mouseup', handleResizeEnd);
            document.removeEventListener('touchmove', handleResize);
            document.removeEventListener('touchend', handleResizeEnd);
            document.removeEventListener('touchcancel', handleResizeEnd);

            // Re-enable text selection
            document.body.style.userSelect = '';
            document.body.style.cursor = '';

            // Re-enable pointer events on all segments
            const allSegmentElements = document.querySelectorAll(`.${styles['segment']}`);
            allSegmentElements.forEach(seg => {
                (seg as HTMLElement).style.pointerEvents = '';
            });

            // Sync the final state to React
            if (this.resizingSegment) {
                this.setState({ segments: this.resizingSegment.currentSegments });
            }

            this.resizingSegment = null;
        };

        document.addEventListener('mousemove', handleResize);
        document.addEventListener('mouseup', handleResizeEnd);
        // Touch events for mobile
        document.addEventListener('touchmove', handleResize);
        document.addEventListener('touchend', handleResizeEnd);
        document.addEventListener('touchcancel', handleResizeEnd);
    };

    updateSegmentDOMPositions(segments: CutSegment[]) {
        // Directly update DOM element styles without React re-render
        segments.forEach((segment) => {
            const element = document.querySelector(`[data-segment-id="${segment.id}"]`) as HTMLElement;
            if (element) {
                const leftPercent = (segment.start / this.videoDuration) * 100;
                const widthPercent = ((segment.end - segment.start) / this.videoDuration) * 100;
                element.style.left = `${leftPercent}%`;
                element.style.width = `${widthPercent}%`;
            }
        });
    }

    updateSegmentBoundary(segmentId: string, edge: 'start' | 'end', newTime: number) {
        const segments = [...this.state.segments];
        const segmentIndex = segments.findIndex(s => s.id === segmentId);

        if (segmentIndex === -1) return;

        const segment = segments[segmentIndex];
        const MIN_SEGMENT_DURATION = 0.5; // Minimum 0.5 seconds

        console.log('📏 Update boundary:', {
            segmentIndex,
            edge,
            requestedTime: newTime.toFixed(2),
            currentSegment: `${segment.start.toFixed(2)} - ${segment.end.toFixed(2)}`
        });

        // Constrain to video bounds
        const originalNewTime = newTime;
        newTime = Math.max(0, Math.min(this.videoDuration, newTime));
        if (newTime !== originalNewTime) {
            console.log('⚠️ Constrained to video bounds:', originalNewTime.toFixed(2), '→', newTime.toFixed(2));
        }

        if (edge === 'start') {
            // Find previous segment
            const prevSegment = segmentIndex > 0 ? segments[segmentIndex - 1] : null;

            // Constraints:
            // 1. Can't make current segment too small
            const maxStart = segment.end - MIN_SEGMENT_DURATION;

            // 2. If there's a previous segment, it can't become too small either
            let minStart = 0;
            if (prevSegment) {
                // Previous segment needs to maintain minimum duration
                minStart = prevSegment.start + MIN_SEGMENT_DURATION;
            }

            // Apply constraints
            const beforeConstraint = newTime;
            newTime = Math.max(minStart, Math.min(maxStart, newTime));
            if (newTime !== beforeConstraint) {
                console.log('⚠️ Constrained:', beforeConstraint.toFixed(2), '→', newTime.toFixed(2));
            }

            console.log('✅ Setting start:', newTime.toFixed(2));
            segment.start = newTime;

            // Adjust previous segment's end to match
            if (prevSegment) {
                console.log('↔️ Adjusting prev segment end to:', newTime.toFixed(2));
                prevSegment.end = newTime;
            }
        } else {
            // Find next segment
            const nextSegment = segmentIndex < segments.length - 1 ? segments[segmentIndex + 1] : null;

            // Constraints:
            // 1. Can't make current segment too small
            const minEnd = segment.start + MIN_SEGMENT_DURATION;

            // 2. If there's a next segment, it can't become too small either
            let maxEnd = this.videoDuration;
            if (nextSegment) {
                // Next segment needs to maintain minimum duration
                maxEnd = nextSegment.end - MIN_SEGMENT_DURATION;
            }

            // Apply constraints
            const beforeConstraint = newTime;
            newTime = Math.max(minEnd, Math.min(maxEnd, newTime));
            if (newTime !== beforeConstraint) {
                console.log('⚠️ Constrained:', beforeConstraint.toFixed(2), '→', newTime.toFixed(2));
            }

            console.log('✅ Setting end:', newTime.toFixed(2));
            segment.end = newTime;

            // Adjust next segment's start to match
            if (nextSegment) {
                console.log('↔️ Adjusting next segment start to:', newTime.toFixed(2));
                nextSegment.start = newTime;
            }
        }

        console.log('✅ Final segments:', segments.map(s => `${s.start.toFixed(2)}-${s.end.toFixed(2)} (${s.action})`).join(', '));

        // Update state directly and force immediate re-render for smooth dragging
        (this.state as any).segments = segments;
        this.forceUpdate();
    }

    updateSegmentTime(segmentId: string, field: 'start' | 'end', value: number) {
        // Handle NaN from empty input
        if (isNaN(value)) return;

        const MIN_SEGMENT_DURATION = 0.5;
        const segments = [...this.state.segments];
        const segmentIndex = segments.findIndex(seg => seg.id === segmentId);
        if (segmentIndex === -1) return;

        const segment = segments[segmentIndex];
        const newValue = Math.max(0, Math.min(this.videoDuration, value));

        if (field === 'start') {
            // Ensure minimum duration
            const maxStart = segment.end - MIN_SEGMENT_DURATION;
            const clampedValue = Math.min(newValue, maxStart);
            segment.start = clampedValue;

            // Adjust previous segment's end to match (prevent gaps)
            if (segmentIndex > 0) {
                segments[segmentIndex - 1].end = clampedValue;
            }
        } else {
            // Ensure minimum duration
            const minEnd = segment.start + MIN_SEGMENT_DURATION;
            const clampedValue = Math.max(newValue, minEnd);
            segment.end = clampedValue;

            // Adjust next segment's start to match (prevent gaps)
            if (segmentIndex < segments.length - 1) {
                segments[segmentIndex + 1].start = clampedValue;
            }
        }

        this.setState({ segments });
    }

    async cutAndDownload() {
        if (this.isFileCutInProgress) {
            this.uiContext.warnToast(
                'Please wait', 'Please wait until current process to be finished', false, false
            );
            return;
        }

        const keepSegments = this.state.segments
            .filter(seg => seg.action === 'keep')
            .sort((a, b) => a.start - b.start);

        if (keepSegments.length === 0) {
            this.uiContext.showErrorDialog({
                title: 'Error', text: 'Please select at least one segment to keep'
            });
            return;
        }

        const fileUpload = this.sourceFileUploadArray[0];
        const fmds = await FileUploadUtils.toUploadInProgressFile<SupportedFileTypes>(fileUpload);
        const fmd: UploadInProgressFile<SupportedFileTypes> = fmds[0];

        // Determine output extension
        const extension = '.' + getExtension((this.sourceFileUploadArray[0].file as any).name);
        const outputExtension = this.selectedToType === 'same'
            ? extension.replace('.', '').toUpperCase()
            : this.selectedToType.replace('.', '').toUpperCase();

        const isAudio = this.pageData.fromType.indexOf(FromTypes.AUDIO) > -1;

        // V2 format options for cutting with full quality
        const cutOptions: Record<string, any> = isAudio ? {
            process: 'CUT',
            toMedia: 'AUDIO',
            toExtension: outputExtension,
            audioCodec: getAudioCodecForExtension(outputExtension),
            compression: 'FILE_SIZE_PERCENTAGE',
            sizePercentage: 100,
            audioChannelSelection: 'AUDIO_CHANNEL',
            audioChannel: 'ORIGINAL',
            audioSampleRateSelection: 'AUDIO_SAMPLE_RATE',
            audioSampleRate: 'ORIGINAL',
        } : {
            process: 'CUT',
            toMedia: 'VIDEO',
            toExtension: outputExtension,
            videoCodec: getVideoCodecForExtension(outputExtension),
            audioCodec: getAudioCodecForExtension(outputExtension),
            videoCompression: 'FILE_SIZE_PERCENTAGE_WITH_AUTO_SCALE',
            sizePercentage: 100,
            autoScale: false,
            videoResolution: 'VIDEO_RESOLUTION_ORIGINAL',
            videoTrimSelection: 'UNCHANGED',
        };

        // Add cut segments in V2 format
        cutOptions.outputCut = keepSegments.map(seg => ({
            startTime: seg.start,
            duration: seg.end - seg.start,
        }));

        const cutFileUpload = this.convertNoneMergeUploadInProgressFilesToFileUpload(fmd, cutOptions);
        this.cutFileUploadArray.splice(0, this.cutFileUploadArray.length);
        this.cutFileUploadArray.unshift(cutFileUpload);
        this.forceUpdate();
        setTimeout(() => {
            if (this.savedFilesRef.current) {
                this.savedFilesRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 100);
        await this.processFile(cutFileUpload, () => {});
    }



    getVideoAndControllers() {
        const currentTime = this.outputVideoCurrentTime || 0;
        const duration = this.videoDuration || 0;
        const progress = (currentTime / duration) * 100 || 0;
        const outputDuration = this.getTotalOutputDuration();

        return <div className={styles['preview-column']}>
            <button className={styles['start-new-btn']} onClick={e => this.startNew()}>
                <span>←</span> Start New
            </button>

            <div className={styles['video-container']}>
                <div className={styles['preview-label']}>Input Preview</div>
                {
                    this.state.isLoading && <div className={styles['loading-overlay']}>
                        <div className={styles['spinner']}></div>
                        <div className={styles['text']}>Processing Media...</div>
                        <div className={styles['sub-text']}>This may take a moment.</div>
                    </div>
                }
                {
                    !this.state.isLoading && !this.fileUrl && this.sourceFileUploadArray && this.sourceFileUploadArray.length > 0 && <div>
                        <FileListItemComponent
                            {...this.props}
                            fileUpload={this.sourceFileUploadArray[0]}>
                        </FileListItemComponent>
                    </div>
                }
                {
                    !this.state.isLoading && this.fileUrl && <div className={styles['video-wrapper']}>
                        <video ref={this.outputVideoRef}
                            onClick={() => this.playOrPauseOutputVideo()}
                            onTimeUpdate={() => this.forceUpdate()}
                            onEnded={() => this.setState({ isPlaying: false })}
                            onLoadedMetadata={e => this.videoLoaded()}>
                            <source src={this.fileObjectURL} />
                            <source src={this.fileUrl} />
                        </video>

                        <div className={styles['custom-controls']}>
                            <div className={styles['progress-container']}>
                                <input
                                    type="range"
                                    min="0"
                                    max={duration}
                                    step="0.1"
                                    value={currentTime}
                                    onChange={(e) => this.handleScrub(e)}
                                    className={styles['scrubber']}
                                    style={{ backgroundSize: `${progress}% 100%` } as any}
                                />
                            </div>
                            <div className={styles['control-buttons']}>
                                <button className={styles['control-icon']} onClick={() => this.playOrPauseOutputVideo()}>
                                    {this.state.isPlaying ?
                                        <img src="/icons/svg/pause.svg" alt="Pause" /> :
                                        <img src="/icons/svg/play.svg" alt="Play" />
                                    }
                                </button>
                                <div className={styles['time']}>
                                    {DateTimeUtils.secondsToTime(currentTime, true)} / {DateTimeUtils.secondsToTime(duration, true)}
                                </div>
                                <div className={styles['spacer']}></div>
                                <button className={styles['control-icon']} onClick={() => this.toggleMute()}>
                                    {this.state.isMuted ?
                                        <img src="/icons/svg/volume-mute.svg" alt="Unmute" /> :
                                        <img src="/icons/svg/volume-up.svg" alt="Mute" />
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                }
            </div>

            {this.state.showOutputPreview && this.hasKeepSegments && this.fileUrl && (
                <div className={styles['video-container']}>
                    <div className={styles['preview-label']}>
                        <span>Output Preview</span>
                        <span className={styles['duration-badge']}>
                            Duration: {DateTimeUtils.secondsToTime(outputDuration, true)}
                        </span>
                    </div>
                    <div className={styles['video-wrapper']}>
                        <canvas
                            ref={this.previewCanvasRef}
                            className={styles['preview-canvas']}
                            onClick={() => this.toggleOutputPreviewPlayback()}
                        />
                        {!this.state.isPlayingPreview && (
                            <div className={styles['preview-overlay']}>
                                <button
                                    className={styles['preview-play-btn']}
                                    onClick={() => this.toggleOutputPreviewPlayback()}
                                >
                                    <img src="/icons/svg/play.svg" alt="Play Preview" />
                                </button>
                                <div className={styles['preview-hint']}>
                                    Click to preview output with only "Keep" segments
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>;
    }

    private getSEOSections() {
        return <div className="mt-5">
            { <div className="mt-3"><FeatureDetailsComponent markdown={this.props.markdown}></FeatureDetailsComponent></div> }
            { <div className="mt-3"><RelatedLinksComponent relatedLinks={this.props.relatedLinks}></RelatedLinksComponent></div> }
            { this.showRating && <div className="text-center mt-3"><RatingDisplayComponent rating={this.props.rating} title={this.title} feature={this.feature} description={this.description}></RatingDisplayComponent></div> }
            { this.showInfoGrid && !this.featureDescriptionsDisabled && <div className="mt-3">{ this.features && <FeaturesTilesComponent features={this.features}></FeaturesTilesComponent> }</div> }
        </div>;
    }

    toggleOutputPreview() {
        this.setState({ showOutputPreview: !this.state.showOutputPreview });
    }

    getKeepSegmentsSorted(): CutSegment[] {
        return this.state.segments
            .filter(seg => seg.action === 'keep')
            .sort((a, b) => a.start - b.start);
    }

    getTotalOutputDuration(): number {
        const keepSegments = this.getKeepSegmentsSorted();
        return keepSegments.reduce((total, seg) => total + (seg.end - seg.start), 0);
    }

    playOutputPreview() {
        if (!this.outputVideoElement || !this.previewCanvasRef.current) return;

        const canvas = this.previewCanvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const keepSegments = this.getKeepSegmentsSorted();
        if (keepSegments.length === 0) return;

        let currentSegmentIndex = 0;
        let segmentStartTime = keepSegments[0].start;

        this.outputVideoElement.currentTime = segmentStartTime;
        this.outputVideoElement.muted = this.state.isMuted;

        const playNextSegment = () => {
            if (currentSegmentIndex >= keepSegments.length) {
                this.setState({ isPlayingPreview: false });
                return;
            }

            const segment = keepSegments[currentSegmentIndex];
            this.outputVideoElement.currentTime = segment.start;

            const checkAndPlay = () => {
                if (this.outputVideoElement.readyState >= 2) {
                    this.outputVideoElement.play().then(() => {
                        const drawFrame = () => {
                            if (!this.state.isPlayingPreview) {
                                this.outputVideoElement.pause();
                                return;
                            }

                            const currentTime = this.outputVideoElement.currentTime;

                            if (currentTime >= segment.end) {
                                this.outputVideoElement.pause();
                                currentSegmentIndex++;
                                playNextSegment();
                                return;
                            }

                            // Draw current frame to canvas
                            if (this.outputVideoElement && canvas) {
                                canvas.width = this.outputVideoElement.videoWidth;
                                canvas.height = this.outputVideoElement.videoHeight;
                                ctx.drawImage(this.outputVideoElement, 0, 0, canvas.width, canvas.height);
                            }

                            this.previewAnimationFrameId = requestAnimationFrame(drawFrame);
                        };

                        drawFrame();
                    }).catch(err => {
                        console.error('Preview playback error:', err);
                        this.setState({ isPlayingPreview: false });
                    });
                } else {
                    setTimeout(checkAndPlay, 100);
                }
            };

            checkAndPlay();
        };

        this.setState({ isPlayingPreview: true });
        playNextSegment();
    }

    pauseOutputPreview() {
        this.setState({ isPlayingPreview: false });
        if (this.previewAnimationFrameId) {
            cancelAnimationFrame(this.previewAnimationFrameId);
            this.previewAnimationFrameId = null;
        }
        if (this.outputVideoElement) {
            this.outputVideoElement.pause();
        }
    }

    toggleOutputPreviewPlayback() {
        if (this.state.isPlayingPreview) {
            this.pauseOutputPreview();
        } else {
            this.playOutputPreview();
        }
    }

    renderSegmentTimeline() {
        if (!this.videoDuration) return null;

        return (
            <div className={styles['timeline-container']}>
                <div className={styles['timeline-instruction']}>
                    <span className={styles['instruction-icon']}>💡</span>
                    <span>Click segments to toggle Keep/Remove. Drag segment edges to resize. Drag playhead handle to scrub. Click ✂️ to split.</span>
                </div>
                <div className={styles['timeline']}>
                    {this.state.segments.map((segment, index) => {
                        const leftPercent = (segment.start / this.videoDuration) * 100;
                        const widthPercent = ((segment.end - segment.start) / this.videoDuration) * 100;
                        const isFirstSegment = index === 0;
                        const isLastSegment = index === this.state.segments.length - 1;

                        return (
                            <div
                                key={segment.id}
                                data-segment-id={segment.id}
                                className={`${styles['segment']} ${styles[segment.action]}`}
                                style={{
                                    left: `${leftPercent}%`,
                                    width: `${widthPercent}%`
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.toggleSegmentAction(segment.id);
                                }}
                                title={`Click to ${segment.action === 'keep' ? 'remove' : 'keep'} this segment`}
                            >
                                {!isFirstSegment && (
                                    <div
                                        className={`${styles['resize-handle']} ${styles['resize-handle-left']}`}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            this.handleSegmentResizeStart(segment.id, 'start', e);
                                        }}
                                        onTouchStart={(e) => {
                                            e.stopPropagation();
                                            this.handleSegmentResizeStart(segment.id, 'start', e as any);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        title="Drag to resize"
                                    />
                                )}
                                <span className={styles['segment-label']}>
                                    {segment.action === 'keep' ? '✓ Keep' : '✗ Remove'}
                                </span>
                                {!isLastSegment && (
                                    <div
                                        className={`${styles['resize-handle']} ${styles['resize-handle-right']}`}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            this.handleSegmentResizeStart(segment.id, 'end', e);
                                        }}
                                        onTouchStart={(e) => {
                                            e.stopPropagation();
                                            this.handleSegmentResizeStart(segment.id, 'end', e as any);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        title="Drag to resize"
                                    />
                                )}
                            </div>
                        );
                    })}
                    <div
                        className={styles['playhead']}
                        style={{ left: `${(this.outputVideoCurrentTime / this.videoDuration) * 100}%` }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className={styles['playhead-cut-btn']}
                            onClick={this.handleCutClick}
                            title="Click to split segment at this position"
                        >
                            ✂️
                        </button>
                        <div
                            className={styles['playhead-drag-handle']}
                            onMouseDown={this.handlePlayheadDragStart}
                            onTouchStart={this.handlePlayheadDragStart}
                            title="Drag to move playhead"
                        >
                            <div className={styles['drag-grip']}></div>
                        </div>
                    </div>
                </div>
                <div className={styles['timeline-labels']}>
                    <span>0:00</span>
                    <span>{DateTimeUtils.secondsToTime(this.videoDuration, true)}</span>
                </div>
            </div>
        );
    }

    renderSegmentsList() {
        return (
            <div className={styles['segments-list']}>
                {this.state.segments.map((segment, index) => (
                    <div key={segment.id} className={`${styles['segment-item']} ${styles[segment.action]}`}>
                        <div className={styles['segment-header']}>
                            <span className={styles['segment-number']}>Segment {index + 1}</span>
                            <div className={styles['header-actions']}>
                                <button
                                    className={styles['action-toggle']}
                                    onClick={() => this.toggleSegmentAction(segment.id)}
                                >
                                    {segment.action === 'keep' ? 'Keep' : 'Remove'}
                                </button>
                                {this.state.segments.length > 1 && (
                                    <button
                                        className={styles['remove-segment']}
                                        onClick={() => this.removeSegment(segment.id)}
                                        title="Delete this segment"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className={styles['segment-times']}>
                            <div className={styles['time-input']}>
                                <label>Start</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={segment.start.toFixed(2)}
                                    onChange={(e) => this.updateSegmentTime(segment.id, 'start', parseFloat(e.target.value))}
                                />
                            </div>
                            <div className={styles['time-input']}>
                                <label>End</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={segment.end.toFixed(2)}
                                    onChange={(e) => this.updateSegmentTime(segment.id, 'end', parseFloat(e.target.value))}
                                />
                            </div>
                        </div>
                        <div className={styles['segment-duration']}>
                            Duration: {DateTimeUtils.secondsToTime(segment.end - segment.start, true)}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    render() {
        if (!this.state.fileSelected) {
            return <div key="no-file" className={`cutter-container ${styles['cutter-container']}`}>
                <div className={styles['hero-section']}>
                    <div className={styles['hero-badge']}>
                        <span className={styles['badge-icon']}>✂️</span>
                        <span>Free Online Tool</span>
                    </div>
                    <h1 className={styles['hero-title']}>{this.title}</h1>
                    <p className={styles['hero-description']}>{this.description}</p>

                    <div className={styles['upload-card']}>
                        <div
                            className={`${styles['upload-area']} ${this.state.isDragging ? styles['upload-area-dragging'] : ''}`}
                            onDragOver={this.handleDragOver}
                            onDragLeave={this.handleDragLeave}
                            onDrop={this.handleDrop}
                        >
                            <div className={styles['upload-icon']}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                            </div>
                            <div className={styles['upload-text']}>
                                <span className={styles['upload-title']}>Drop your file here, or browse</span>
                                <span className={styles['upload-subtitle']}>Supports MP4, MOV, AVI, MKV, WebM, MP3, WAV and more</span>
                            </div>
                            <FileUploadComponent
                                fileSelected={$event => this.fileSelected($event)}
                                singleFileSelect={true}
                                fileSelectButtonClicked={() => { }}
                                acceptedFileTypes={this.pageData?.accept}
                                uploadBtnText="Select File"
                                uploadBtnStyles={{}}
                                uploadBtnClassName={styles['upload-btn']}>
                            </FileUploadComponent>
                        </div>
                        <div className={styles['upload-footer']}>
                            <span>🔒 Your files are processed securely and never stored</span>
                        </div>
                    </div>

                    <div className={styles['features-grid']}>
                        <div className={styles['feature-item']}>
                            <div className={styles['feature-icon']}>⚡</div>
                            <div className={styles['feature-content']}>
                                <h3>Lightning Fast</h3>
                                <p>Process files in seconds with our optimized servers</p>
                            </div>
                        </div>
                        <div className={styles['feature-item']}>
                            <div className={styles['feature-icon']}>🎯</div>
                            <div className={styles['feature-content']}>
                                <h3>Precise Cutting</h3>
                                <p>Frame-accurate cuts with intuitive timeline controls</p>
                            </div>
                        </div>
                        <div className={styles['feature-item']}>
                            <div className={styles['feature-icon']}>💎</div>
                            <div className={styles['feature-content']}>
                                <h3>No Quality Loss</h3>
                                <p>Maintain original quality with smart re-encoding</p>
                            </div>
                        </div>
                    </div>
                </div>

                {this.getSEOSections()}
            </div>
        }

        return <div key="has-file" className={`cutter-container ${styles['cutter-container']}`}>
            <div className="gcontent-container mb-4">
                <MotiveComponent
                    data={AdSlots.belowTool} containerWidth="100%" containerHeight="60px"
                    location={MotiveLocationTypes.convertPageBelowFeatureTitle} adWidth="720px" adHeight="60px" showAds={true}
                    className='width_major_md'
                ></MotiveComponent>
            </div>

            <div className={styles['editor-wrapper']}>
                {this.getVideoAndControllers()}

                <div className={styles['controls-column']}>
                    <div className={styles['control-group']}>
                        <label className={styles['group-title']}>Timeline & Playback</label>

                        <div className={styles['playback-controls']}>
                            <button disabled={!this.fileUrl} className={styles['control-btn']} onClick={e => this.seekOutputVideo(-5)} title="-5 Seconds">
                                -5s
                            </button>
                            <button disabled={!this.fileUrl} className={`${styles['control-btn']} ${styles['play-btn']}`} onClick={e => this.playOrPauseOutputVideo()} title="Play/Pause">
                                <img src={this.outputVideoElement?.paused ? "/icons/svg/play.svg" : "/icons/svg/pause.svg"} alt="Play/Pause" />
                            </button>
                            <button disabled={!this.fileUrl} className={styles['control-btn']} onClick={e => this.seekOutputVideo(5)} title="+5 Seconds">
                                +5s
                            </button>
                        </div>

                        {this.renderSegmentTimeline()}
                    </div>

                    <div className={styles['control-group']}>
                        <label className={styles['group-title']}>Cut Segments</label>
                        {this.renderSegmentsList()}
                    </div>

                    <div className={styles['control-group']}>
                        <label className={styles['group-title']}>Output Setting</label>
                        <div className={styles['format-selector']}>
                            <Dropdown
                                values={this.supportedAVTypes}
                                initialValue={this.supportedAVTypes.find(t => t.item === this.selectedToType)}
                                valueFormatter={(o) => o.text}
                                onSelect={(v) => this.updateToVideoType(v)}
                                hasTooltip={() => false}
                                getTooltip={() => ''}
                            />
                        </div>
                    </div>

                    <div className={styles['action-section']}>
                        <button
                            className={styles['download-btn']}
                            onClick={() => this.cutAndDownload()}
                            disabled={!this.hasKeepSegments || !!this.shouldShowLoading}
                        >
                            {this.shouldShowLoading ? 'Processing...' : 'Cut & Download'}
                            {!this.shouldShowLoading && <img src="/icons/svg/download.svg" className="white-icon" width="20" height="20" alt="" style={{ filter: 'brightness(0) invert(1)' }} />}
                        </button>
                        {!this.hasKeepSegments && (
                            <div className={styles['download-hint']}>
                                Select at least one segment to keep
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div ref={this.savedFilesRef} className={styles['saved-files']}>
                {
                    this.cutFileUploadArray && this.cutFileUploadArray.length > 0 && <div className="mt-4">
                        <h4 className="mb-3">Processed Files</h4>
                        {this.cutFileUploadArray.map((file, i) => (
                            <FileListItemComponent key={file.id} {...this.props} fileUpload={file} />
                        ))}
                    </div>
                }
            </div>

        </div>;
    }
}
